import { useEffect, useState } from "react";
import { fetchAllPages } from "./pages";
import { ApiError, get } from "./api";
import { bus } from "./bus";

interface ApiState<T> {
  data: T | null;
  loading: boolean;
  error: ApiError | null;
}

/**
 * Minimal data-fetching hook. Re-fetches when `path` changes.
 *
 * Also re-fetches when `data:changed` fires on the bus — the api
 * client emits that after every successful POST/PATCH/DELETE, so a
 * vehicle created in a modal causes the dropdown elsewhere on the
 * page to refresh without an explicit reload call.
 */
export function useApi<T>(path: string | null): ApiState<T> & { reload: () => void } {
  const [state, setState] = useState<ApiState<T>>({ data: null, loading: !!path, error: null });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!path) {
      setState({ data: null, loading: false, error: null });
      return;
    }
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    get<T>(path).then(
      (data) => { if (!cancelled) setState({ data, loading: false, error: null }); },
      (error: ApiError) => { if (!cancelled) setState({ data: null, loading: false, error }); }
    );
    return () => { cancelled = true; };
  }, [path, tick]);

  // Subscribe once to the bus. We deliberately don't filter by URL —
  // pretty much any mutation could affect any list, and a re-GET is
  // cheap enough that aggressive invalidation is the right default.
  // Screens that need to opt out can use the bare `get()` function.
  useEffect(() => {
    return bus.on("data:changed", () => { setTick((t) => t + 1); });
  }, []);

  return { ...state, reload: () => setTick((t) => t + 1) };
}

/**
 * Like useApi, but follows every page and returns `{ data: rows }` so the
 * usual unwrap helpers still work. For views that need the whole set
 * (org chart, calendars) — see lib/pages.ts.
 */
export function useAllPages<T>(path: string | null): ApiState<{ data: T[] }> & { reload: () => void } {
  const [state, setState] = useState<ApiState<{ data: T[] }>>({ data: null, loading: !!path, error: null });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!path) {
      setState({ data: null, loading: false, error: null });
      return;
    }
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    fetchAllPages<T>(path).then(
      (rows) => { if (!cancelled) setState({ data: { data: rows }, loading: false, error: null }); },
      (error: ApiError) => { if (!cancelled) setState({ data: null, loading: false, error }); }
    );
    return () => { cancelled = true; };
  }, [path, tick]);

  useEffect(() => bus.on("data:changed", () => { setTick((t) => t + 1); }), []);

  return { ...state, reload: () => setTick((t) => t + 1) };
}
